import { useCallback, useEffect, useRef, useState } from "react";

export type PollState<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
  lastUpdated: Date | null;
  refetch: () => void;
};

export interface UsePolledDataOpts {
  /** Refresh interval in ms. 0 disables auto-refresh. Default: 15_000. */
  intervalMs?: number;
  /** Skip the request when false (e.g. waiting for a brandId). */
  enabled?: boolean;
  /** Refetch immediately when any of these values change. */
  deps?: ReadonlyArray<unknown>;
}

/**
 * Generic polled-data hook used by every dashboard page. Avoids stale-closure
 * problems by only calling the latest `fetcher` and treats every `deps`
 * change as a "refetch immediately" signal (the previous in-flight request
 * is ignored by tracking a monotonic ticket).
 */
export const usePolledData = <T,>(
  fetcher: () => Promise<T>,
  { intervalMs = 15_000, enabled = true, deps = [] }: UsePolledDataOpts = {},
): PollState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const ticketRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async () => {
    if (!enabled) return;
    const ticket = ++ticketRef.current;
    setLoading(true);
    try {
      const result = await fetcherRef.current();
      if (ticket !== ticketRef.current) return; // a newer request superseded us
      setData(result);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      if (ticket !== ticketRef.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (ticket === ticketRef.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    void run();
    if (intervalMs > 0) {
      const id = window.setInterval(run, intervalMs);
      return () => window.clearInterval(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, ...deps]);

  return { data, error, loading, lastUpdated, refetch: run };
};
